/**
 * 工作区圈选 UI：跨页 / workspace 元素逐条选框 + 联合包围盒与四角手柄（世界坐标）。
 */
import { useMemo } from 'react';
import type { Element, Page } from '@/components/canvas/document/publicTypes';
import type { ResizeHandle } from '@/components/canvas/transform';
import { SelectionBoxLayer } from '@/components/canvas/selection/SelectionBoxLayer';
import { UnionSelectionChrome } from '@/components/canvas/selection/UnionSelectionChrome';
import {
  elementToWorldElement,
  pageSurfaceWorldBounds,
  unionBoundsFromRects,
} from '@/components/canvas/selection/unionBounds';
import { createPagePseudoElement } from '@/components/canvas/selection/pageSelection';

export interface WorldSelectionOverlayProps {
  pages: Page[];
  workspaceElements: Element[];
  selectedElementIds: string[];
  selectedPageIds: string[];
  currentPageId: string;
  zoom: number;
  elementDrag: boolean;
  dragOffset: { dx: number; dy: number };
  onStartUnionResize: (handle: ResizeHandle, e: React.MouseEvent) => void;
}

export function WorldSelectionOverlay({
  pages,
  workspaceElements,
  selectedElementIds,
  selectedPageIds,
  currentPageId,
  zoom,
  elementDrag,
  dragOffset,
  onStartUnionResize,
}: WorldSelectionOverlayProps) {
  const worldElements = useMemo(() => {
    const out: Element[] = [];
    const applyDrag = (el: Element, onActivePage: boolean) => {
      if (!elementDrag || !onActivePage) return el;
      return {
        ...el,
        layout: {
          ...el.layout,
          x: el.layout.x + dragOffset.dx,
          y: el.layout.y + dragOffset.dy,
        },
      };
    };

    // 工作区圈选已选 Page 时只展示页框，不为页内元素绘制选框
    if (selectedPageIds.length === 0) {
      for (const page of pages) {
        const { x: px, y: py } = page.workspaceLayout;
        for (const el of page.elements) {
          if (!selectedElementIds.includes(el.id) || el.type === 'line' || el.type === 'table') continue;
          const world = elementToWorldElement(el, px, py);
          out.push(applyDrag(world, page.id === currentPageId));
        }
      }
    }
    for (const el of workspaceElements) {
      if (!selectedElementIds.includes(el.id) || el.type === 'line' || el.type === 'table') continue;
      out.push(applyDrag(el, true));
    }
    return out;
  }, [pages, workspaceElements, selectedElementIds, selectedPageIds, currentPageId, elementDrag, dragOffset]);

  const pageBoxElements = useMemo(() => {
    return selectedPageIds.map((pageId) => {
      const page = pages.find((p) => p.id === pageId);
      if (!page) return null;
      const b = pageSurfaceWorldBounds(page);
      const pseudo = createPagePseudoElement(b.width, b.height);
      pseudo.layout = { ...pseudo.layout, x: b.x, y: b.y };
      return pseudo;
    }).filter(Boolean) as Element[];
  }, [selectedPageIds, pages]);

  const unionRect = useMemo(() => {
    const elementRects = worldElements.map((el) => {
      const { x, y, width, height } = {
        x: el.layout.x,
        y: el.layout.y,
        width: el.size.width,
        height: el.size.height,
      };
      return { x, y, width, height };
    });
    const pageRects = selectedPageIds
      .map((id) => pages.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => pageSurfaceWorldBounds(p!));
    return unionBoundsFromRects([...elementRects, ...pageRects]);
  }, [worldElements, selectedPageIds, pages]);

  const showUnion =
    unionRect != null &&
    (worldElements.length + selectedPageIds.length >= 2 ||
      (worldElements.length >= 1 && selectedPageIds.length >= 1));

  if (elementDrag) return null;
  if (worldElements.length === 0 && pageBoxElements.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-[7]" aria-hidden>
      {pageBoxElements.map((pseudo) => (
        <SelectionBoxLayer
          key={`page-box-${pseudo.id}-${pseudo.layout.x}`}
          elements={[pseudo]}
          dragOffset={{ dx: 0, dy: 0 }}
          zoom={zoom}
        />
      ))}

      {worldElements.length > 0 && (
        <SelectionBoxLayer elements={worldElements} dragOffset={{ dx: 0, dy: 0 }} zoom={zoom} />
      )}

      {showUnion && unionRect && (
        <UnionSelectionChrome
          rect={unionRect}
          zoom={zoom}
          hideBorder={false}
          onStartUnionResize={onStartUnionResize}
        />
      )}
    </div>
  );
}
